import { yupResolver } from '@hookform/resolvers/yup'
import useTranslation from 'next-translate/useTranslation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { changeUserPassword } from '@/api/settings/changeUserPassword'
import TextField from '@/components/ui/TextField'
import ButtonText from '@/components/ui/Typo/ButtonText'
import Subtitle from '@/components/ui/Typo/Subtitle'
import { IconEye, IconEyeClosed } from '@/res/icons'
import { NAMESPACE_ERRORS, NAMESPACE_SETTINGS_PAGE } from '@/res/namespaces'
import { changePasswordValidationSchema } from '@/res/validationSchemas'

import Spinner from '../ui/Spinner'

interface IForm {
  password: string
  password_confirmation: string
  current_password: string
}

const ChangePasswordForm: React.FC = () => {
  const { t } = useTranslation(NAMESPACE_SETTINGS_PAGE)
  const { t: te } = useTranslation(NAMESPACE_ERRORS)

  const [pending, setPending] = useState(false)

  const onSuccess = () => toast.success(t('password_change_success'))

  const defaultValues = {
    current_password: '',
    password: '',
    password_confirmation: '',
  }

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IForm>({
    defaultValues,
    resolver: yupResolver(changePasswordValidationSchema(te)),
    mode: 'all',
  })

  const [isShowing, setIsShowing] = useState(false)

  const onSubmit = handleSubmit(async (values) => {
    try {
      setPending(true)
      await changeUserPassword(values)
      onSuccess()
      reset(defaultValues)
    } catch (err: any) {
      const errors = err.response?.data?.errors || {}

      Object.keys(errors).forEach((fieldErr) => {
        setError(fieldErr as keyof IForm, {
          message: errors[fieldErr],
        })
      })
    } finally {
      setPending(false)
    }
  })

  const togglePasswordShowing = () => {
    setIsShowing((prevState) => !prevState)
  }

  const passwordShowing = (
    <div className="absolute right-4 top-3">
      {isShowing ? (
        <IconEyeClosed
          className="cursor-pointer text-primary"
          onClick={togglePasswordShowing}
        />
      ) : (
        <IconEye
          className="cursor-pointer text-primary"
          onClick={togglePasswordShowing}
        />
      )}
    </div>
  )

  const passwordError = errors.password
  const currentPasswordError = errors.current_password
  const passwordConfirmationError = errors.password_confirmation

  return (
    <div className="rounded-lg border border-gray-300 mt-10 py-7 small:px-10 px-5">
      <Subtitle>{t('change_password')}</Subtitle>
      <form className="mt-8" onSubmit={onSubmit}>
        <TextField
          inputProps={{
            type: isShowing ? 'text' : 'password',
            required: true,
            ...register('current_password'),
          }}
          wrapperClassName="max-w-xs"
          label={t('current_password')}
          error={currentPasswordError}
          customComponent={passwordShowing}
        />
        <div className="flex items-start justify-between flex-wrap gap-x-4">
          <TextField
            inputProps={{
              type: isShowing ? 'text' : 'password',
              required: true,
              ...register('password'),
            }}
            error={passwordError}
            wrapperClassName="max-w-xs"
            label={t('new_password')}
          />
          <TextField
            inputProps={{
              type: isShowing ? 'text' : 'password',
              required: true,
              ...register('password_confirmation'),
            }}
            wrapperClassName="max-w-xs"
            label={t('re_enter_new_password')}
            error={passwordConfirmationError}
          />
        </div>
        <div className="flex tablet:justify-end">
          <button className="rounded-lg max-w-xs w-full py-3 bg-primary hover:opacity-90 active:scale-x-95 transition-all">
            {pending ? (
              <span className="flex justify-center w-full">
                <Spinner />
              </span>
            ) : (
              <ButtonText size="small">{t('save')}</ButtonText>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ChangePasswordForm
